/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import {loadLandmarkGeoFences} from '@ion/core/src/services/ArcGIS';
import {GeoFenceAttributes} from '@ion/core/src/types/entities';
import useRootManager from '../../client/useRootManager';
import useThemeContext from '../../theme/useThemeContext';
import GeofenceDetailedViewPopup from '../components/GeofenceDetailedViewPopup';
import {ALL_FEATURES_DEF, EXTENT_FACTOR, mapViewGoToOptions} from '../constants';
import {
  createGeofenceGraphic,
  GEOFENCE_FIELDS,
  GEOFENCE_LAYER_ID,
  getGeofenceDefinition,
  getGeofenceRenderer,
  getGeometry,
  getLabelClass,
  hasGeofenceChanged,
  LandmarkGeofenceLayersProps,
  setLabelVisibility,
  updateGeofenceRenderer
} from '../helpers/geofenceLayer';
import {getUnappliedItems, queueLayerItems} from '../helpers/layerEditQueue';
import {addGraphicsToLayer} from '../helpers/LayerUtils';
import {applyEditsToLayer, getLayerIndex, layerExists} from '../helpers/MapViewUtils';
import {isGeoFenceEnabled, LayerChooserProps} from './LayerChooser';

/**
 * @LandmarkGeofenceLayer
 *
 * Defines the LandmarkGeofenceLayer which manages rendering a layer containing Geofences
 *
 *
 * ```typescript
 *
 *   import LandmarkGeofenceLayer from '@ion/dls/src/maps/layers/LandmarkGeofenceLayer';
 *   
 *   <LandmarkGeofenceLayer  props={props} />
 *
 * ```
 */
export const LandmarkGeofenceLayer = ({
  map,
  layers,
  isGeofenceAccess,
  view: mapView,
  geofences,
  searchResult,
  globalGroupId,
  savingGeofence,
  geofenceUpdates,
  setGeofenceState,
  editGeofenceGraphic,
  onResetGeofenceStatus,
  setEditGeofenceGraphic,
  canAddGeofence :canEditGeofence,
  layerChooserProps: {
    findLabel,
    isRootAccount,
    onFindNearyBy,
    onLayersChange,
    geofenceStatus,
    isFindNearbyPopup,
    selectedGeoFences,
    geofenceLabelState,
    ancestorAccountIds,
    descendantAccountId,
    onFindButtonStateChange,
    geofenceOpacity: opacity,
    onFindNearbyPopupStatusChange,
    setFindNearbyPopupForGeofence,
    geofenceBorderOnly: borderOnly,
    onFindNearbyBreadcrumbBackClick,
    onGeofenceOpacityChange: onOpacityChange
  } = {} as LayerChooserProps,
  isTrackingV2 = false
}: LandmarkGeofenceLayersProps) => {
  const geofenceLayerRef                              = useRef(null);
  const viewClickHandlerRef                           = useRef(null);
  const geofenceLayerViewRef                          = useRef(null);
  const ancestorAccountIdsRef                         = useRef(null);
  const descendantAccountIdRef                        = useRef(null);
  const enabledlayersRef                              = useRef(layers);
  const appliedGeofencesRef                           = useRef(new WeakSet<object>());
  const pendingGeofencesRef                           = useRef<GeoFenceAttributes[]>([]);
  const layerGroupIdRef                               = useRef<number | null>(null);
  const isReplacingGeofencesRef                       = useRef(false);
  const [isGeofenceLoaded, setIsGeofenceLoaded]       = useState(false);
  const {render, createContainer}                     = useRootManager();
  const {isCurrent: isCurrentTheme}                   = useThemeContext();
  const [hideGraphicsForEdit, setHideGraphicsForEdit] = useState<__esri.Graphic>();
  const [defaultLayer, setSelected]                   = useState(isGeoFenceEnabled(layers));
  
  const resetState = useCallback(() => {
    setHideGraphicsForEdit(null);
    onResetGeofenceStatus(null);
  }, []);

  const getAllFeatureAttributes = useCallback(async() => {
    const features = await geofenceLayerRef.current?.queryFeatures(ALL_FEATURES_DEF)
      .then(({features}) => features);
    return features?.map(feature => feature?.attributes);
  }, []);

  // hydrates the given group's cached geofences from the layer's features - skipped
  // while the layer is being replaced with another group's geofences, as its
  // features then belong to neither group ...
  const hydrateGeofenceState = useCallback(throttle(async(globalGroupId: number) => {
    if (globalGroupId && !isReplacingGeofencesRef.current) {
      const attributes = await getAllFeatureAttributes();
      setGeofenceState(attributes, globalGroupId);
    }
  }, 5000, {trailing: false}), [setGeofenceState]);

  const changeOpacity = useCallback(opacity => {
    if (geofenceLayerRef.current) {
      (geofenceLayerRef.current as any).opacity = opacity;
      onOpacityChange(opacity);
    }
  }, []);

  useEffect(() => {
    if (geofenceLayerRef.current && hideGraphicsForEdit?.attributes?.geofenceId) {
      const groupsDefinition = loadLandmarkGeoFences(
        ancestorAccountIds,
        descendantAccountId,
        isRootAccount
      );
      const hideSelectedEditGeofenceQuery = getGeofenceDefinition({
        groupsDefinition,
        operator : '<>',
        value    : hideGraphicsForEdit?.attributes?.geofenceId
      });
      geofenceLayerViewRef.current.filter = {
        where : hideSelectedEditGeofenceQuery
      };
    }
  }, [hideGraphicsForEdit]);

  useEffect(() => {
    if (geofenceLayerRef.current && !editGeofenceGraphic && !savingGeofence) {
      const delay = setTimeout(() => {
        geofenceLayerViewRef.current.filter = {};
        setHideGraphicsForEdit(null);
        clearTimeout(delay);
      }, 500);
    }
  }, [editGeofenceGraphic, savingGeofence]);

  useEffect(() => {
    if (geofenceLayerRef.current && searchResult?.isGeofence) {
      mapView.whenLayerView(geofenceLayerRef.current).then(() => {
        const query            = geofenceLayerRef.current?.createQuery();
        const groupsDefinition = loadLandmarkGeoFences(
          ancestorAccountIds,
          descendantAccountId,
          isRootAccount
        );
        query.where = getGeofenceDefinition({
          groupsDefinition,
          operator : '=',
          value    : searchResult?.item?.OBJECTID
        });
        geofenceLayerRef.current.queryFeatures(query).then(result => {
          const selectedGeofence = result.features[0];
          if (selectedGeofence) {
            const {extent} = selectedGeofence?.geometry;
            mapView.goTo(extent?.expand(EXTENT_FACTOR), mapViewGoToOptions).then(e => {
              if (!enabledlayersRef.current?.includes(GEOFENCE_LAYER_ID)) {
                onLayersChange([...enabledlayersRef.current, GEOFENCE_LAYER_ID]);
              }
            });
          }
        });
      });
    }
  }, [searchResult]);

  useEffect(() => {
    const {current} = geofenceLayerRef;
    if (current && geofenceLayerViewRef.current) {
      if (selectedGeoFences?.length) {
        setSelected(true);
        const selectedGeofenceIds = selectedGeoFences?.map(({id}) => id);
        const groupsDefinition = loadLandmarkGeoFences(
          ancestorAccountIds,
          descendantAccountId,
          isRootAccount
        );
        const definition = getGeofenceDefinition({
          groupsDefinition,
          operator               : 'IN',
          value                  : `(${selectedGeofenceIds})`,
          isSelectedGeofenceOnly : true
        });
        geofenceLayerViewRef.current.filter = {
          where : definition
        };
        const query = geofenceLayerRef?.current?.createQuery();
        query.where = definition;
        current.queryExtent(query).then(({extent}) => {
          mapView.goTo(extent?.expand(EXTENT_FACTOR), mapViewGoToOptions);
        });
      } else {
        geofenceLayerViewRef.current.filter = {};
      }
    } else if (selectedGeoFences?.length) {
      setSelected(true);
    }
  }, [
    isGeofenceLoaded,
    selectedGeoFences,
    ancestorAccountIds,
    descendantAccountId,
    geofenceLayerRef.current
  ]);

  useEffect(() => {
    updateGeofenceRenderer(geofenceLayerRef.current, borderOnly);
  }, [borderOnly]);

  useEffect(() => {
    setLabelVisibility(geofenceLayerRef.current, geofenceLabelState);
  }, [geofenceLabelState]);

  useEffect(() => {
    if (geofenceLayerRef.current) {
      geofenceLayerRef.current.opacity = opacity;
    }
  }, [opacity]);

  useEffect(() => {
    ancestorAccountIdsRef.current = ancestorAccountIds;
  }, [ancestorAccountIds]);

  useEffect(() => {
    descendantAccountIdRef.current = descendantAccountId;
  }, [descendantAccountId]);

  useEffect(() => {
    return () => {
      hydrateGeofenceState(layerGroupIdRef.current);
      viewClickHandlerRef.current?.remove('immediate-click', popupHandler);
      removeAllFeatures();
    };
  }, []);

  // the layer still holds the previous group's geofences here (they are replaced by
  // the geofences effect below), so they hydrate that group's cache - keyed by the
  // group they belong to, never the newly selected one (e.g. the map is kept mounted
  // while the group changes on another page) ...
  useEffect(() => {
    if (globalGroupId && layerGroupIdRef.current !== globalGroupId) {
      hydrateGeofenceState(layerGroupIdRef.current);
    }
  }, [globalGroupId]);

  useEffect(() => {
    if (defaultLayer) {
      // current theme: one popup handler at a time ...
      if (isCurrentTheme) {
        viewClickHandlerRef.current?.remove?.();
      }
      viewClickHandlerRef.current = mapView?.on('immediate-click', popupHandler);
    } else {
      viewClickHandlerRef.current?.remove?.('immediate-click', popupHandler);
    }
  }, [defaultLayer]);

  useEffect(() => {
    enabledlayersRef.current = layers;
    if (geofenceLayerRef.current) {
      const isVisible = isGeoFenceEnabled(layers);
      geofenceLayerRef.current.visible = isVisible;
      setSelected(isVisible);
    }
  }, [layers]);

  useEffect(() => {
    if (geofenceLayerRef.current?.popupTemplate?.content) {
      geofenceLayerRef.current.popupTemplate.content = popupTemplateContent;
    }
  }, [ancestorAccountIds]);

  useEffect(() => {
    if (hasGeofenceChanged(geofenceStatus)) {
      if (hideGraphicsForEdit) {
        removeFeature(hideGraphicsForEdit.attributes.geofenceId);
      }
      setSelected(true);
      if (geofenceLayerRef.current) {
        geofenceLayerRef.current.visible = true;
      }
    } else if (geofenceStatus?.isDelete && geofenceStatus?.success) {
      const {attributes} = hideGraphicsForEdit || {};
      if (attributes) {
        removeFeature(attributes.geofenceId, resetState);
      }
    }
  }, [geofenceStatus]);

  // current theme: `geofences` holds every loaded page, so each new page used
  // to re-send (delete and re-add) all the earlier pages' polygons too - only
  // geofences not yet on the layer are sent now. The layer is still created
  // from the full list, and an empty list still goes through as before. The
  // legacy theme sends the full list, as before ...
  //
  // the layer outlives a global group change (unless the web map changes too), and
  // both paths above only add or replace geofences by id - the first geofences of
  // another group replace all of the layer's features instead, so the previous
  // group's geofences are not left on the map ...
  useEffect(() => {
    const hasGeofences = Array.isArray(geofences) && !!geofences.length;
    const isNewGroup   = !!geofenceLayerRef.current && layerGroupIdRef.current !== globalGroupId;
    const skipApplied  = isCurrentTheme && hasGeofences && !!geofenceLayerRef.current;
    const data         = skipApplied ? getUnappliedItems(geofences, appliedGeofencesRef.current) : geofences;

    if (isNewGroup) {
      replaceAllGeofences(hasGeofences ? geofences : []);
    } else if (!hasGeofences || data.length) {
      addGraphicsToLayer({
        layer     : geofenceLayerRef.current,
        data,
        create    : createGeofenceLayer,
        addUpdate : queueGeofences
      });
      if (skipApplied) {
        markGeofencesApplied(data);
      }
    }
    if (geofenceLayerRef.current) {
      layerGroupIdRef.current = globalGroupId;
    }
  }, [geofences]);

  useEffect(() => {
    if (geofenceUpdates?.length) {
      const params = {
        layer     : geofenceLayerRef.current,
        data      : geofenceUpdates,
        create    : createGeofenceLayer,
        addUpdate : queueGeofences
      };
      const groupId = globalGroupId;
      const delay   = setTimeout(() => {
        // skips updates of a group that is no longer on the layer ...
        if (!layerGroupIdRef.current || layerGroupIdRef.current === groupId) {
          addGraphicsToLayer(params);
        }
        clearTimeout(delay);
      }, 300);
    }
  }, [geofenceUpdates]);

  const createGeofenceLayer = useCallback((geofences: GeoFenceAttributes[]) => {
    if (!geofenceLayerRef.current && !layerExists(map, GEOFENCE_LAYER_ID)) {
      const {layers}   = map;
      const graphics   = createGeofenceGraphic(geofences);
      const labelClass = getLabelClass();
      geofenceLayerRef.current = new FeatureLayer({
        title            : GEOFENCE_LAYER_ID,
        id               : GEOFENCE_LAYER_ID,
        fields           : GEOFENCE_FIELDS as any,
        renderer         : getGeofenceRenderer(borderOnly),
        objectIdField    : 'OBJECTID',
        geometryType     : 'polygon',
        spatialReference : {wkid: 102100},
        outFields        : ['*'],
        source           : graphics,
        labelingInfo     : [labelClass] as any,
        popupEnabled     : false,
        popupTemplate    : {
          title               : '{landmarkName}',
          content             : popupTemplateContent,
          outFields           : ['*'],
          lastEditInfoEnabled : false
        }
      });
      layers.push(geofenceLayerRef.current);
      layers.reorder(geofenceLayerRef.current, getLayerIndex(map, GEOFENCE_LAYER_ID));
      geofenceLayerRef.current.visible = isGeoFenceEnabled(enabledlayersRef.current);
      // current theme: one popup handler at a time - a second registration
      // only repeated the same hit test on every click ...
      if (isCurrentTheme) {
        viewClickHandlerRef.current?.remove?.();
      }
      viewClickHandlerRef.current = mapView?.on('immediate-click', popupHandler);
      geofenceLayerRef.current?.when(async() => {
        geofenceLayerViewRef.current = await mapView.whenLayerView(geofenceLayerRef.current);
      });
      if (selectedGeoFences?.length) {
        setIsGeofenceLoaded(true);
      }
      changeOpacity(opacity);
    }
  }, [
    map,
    mapView,
    borderOnly,
    isRootAccount,
    selectedGeoFences,
    ancestorAccountIds,
    descendantAccountId
  ]);

  // current theme: applies everything queued since the last flush - the
  // debounce keeps only its last call's geofences, dropping a page or an
  // update that arrived within the same 100ms. The legacy theme passes its
  // geofences straight in, as before ...
  const AddUpdateGeofences = useCallback(debounce((latestGeofences?: GeoFenceAttributes[]) => {
    const geofences             = latestGeofences ?? pendingGeofencesRef.current;
    pendingGeofencesRef.current = [];
    if (geofenceLayerRef.current) {
      const graphics = createGeofenceGraphic(geofences);
      const query    = geofenceLayerRef?.current?.createQuery();
      query.where    = `geofenceId IN (${graphics?.map(graphic => graphic?.attributes?.geofenceId)})`;
      geofenceLayerRef.current?.queryFeatures(query).then(({features}) => {
        applyEditsToLayer(
          geofenceLayerRef.current,
          {
            deleteFeatures : features,
            addFeatures    : graphics
          }
        );
      });
    }
  }, 100), [applyEditsToLayer]);

  const queueGeofences = useCallback((geofences: GeoFenceAttributes[]) => {
    if (isCurrentTheme) {
      pendingGeofencesRef.current = queueLayerItems(pendingGeofencesRef.current, geofences, 'geofenceId');
      AddUpdateGeofences();
    } else {
      AddUpdateGeofences(geofences);
    }
  }, [AddUpdateGeofences, isCurrentTheme]);

  const removeFeature = useCallback(async(geofenceId: number, callback?: Function) => {
    if (geofenceId) {
      const query = geofenceLayerRef?.current?.createQuery();
      query.where = `geofenceId = ${geofenceId}`;
      await geofenceLayerRef.current?.queryFeatures(query)
        .then(({features}) => {
          applyEditsToLayer(
            geofenceLayerRef.current,
            {deleteFeatures: features},
            callback
          );
        });
    }
  }, [applyEditsToLayer]);

  const markGeofencesApplied = useCallback((geofences: GeoFenceAttributes[]) => {
    geofences?.forEach(function(geofence) {
      if (geofence && typeof geofence === 'object') {
        appliedGeofencesRef.current.add(geofence);
      }
    });
  }, []);

  // replaces every feature on the layer with the given geofences, in a single edit,
  // dropping anything still queued for the previous geofences ...
  const replaceAllGeofences = useCallback((geofences: GeoFenceAttributes[]) => {
    const layer = geofenceLayerRef.current;
    AddUpdateGeofences.cancel();
    pendingGeofencesRef.current     = [];
    appliedGeofencesRef.current     = new WeakSet<object>();
    isReplacingGeofencesRef.current = true;
    markGeofencesApplied(geofences);
    layer?.queryFeatures(ALL_FEATURES_DEF).then(({features}) => {
      applyEditsToLayer(layer, {
        deleteFeatures : features,
        addFeatures    : createGeofenceGraphic(geofences)
      }, function() {
        isReplacingGeofencesRef.current = false;
      });
    });
  }, [AddUpdateGeofences, applyEditsToLayer, markGeofencesApplied]);

  const removeAllFeatures = useCallback(async() => {
    await geofenceLayerRef.current?.queryFeatures(ALL_FEATURES_DEF)
      .then(({features}) => {
        applyEditsToLayer(
          geofenceLayerRef.current,
          {deleteFeatures: features}
        );
      });
  }, [applyEditsToLayer]);

  const popupTemplateContent = useCallback(feature => {
    const {graphic} = feature;
    geofenceLayerRef.current.popupEnabled = false;
    if (!graphic?.geometry) {
      return getGeometry(
        geofenceLayerRef.current,
        graphic?.attributes?.geofenceId
      ).then(geometry => {
        feature.graphic.geometry = geometry;
        return getPopupContent(feature);
      });
    }
    return getPopupContent(feature);
  }, [ancestorAccountIds]);

  const popupHandler = useCallback(function({x, y}) {
    if (isGeoFenceEnabled(enabledlayersRef.current)) {
      mapView.hitTest({x, y}).then(onResults);
    }
  }, []);
  
  const onResults = useCallback(function({results}) {
    if (results?.length && geofenceLayerRef.current) {
      const {layer} = results?.[0]?.graphic || {};
      geofenceLayerRef.current.popupEnabled = layer?.id === GEOFENCE_LAYER_ID;
    }
  }, [GEOFENCE_LAYER_ID]);

  const getPopupContent = useCallback(feature => {
    const container = createContainer('geofence-popup-container');
    feature.graphic.attributes.OBJECTID = feature.graphic.attributes.geofenceId;
    render(
      <GeofenceDetailedViewPopup isTrackingV2
        isGeofenceAccess={isGeofenceAccess}
        mapView={mapView}
        findLabel={findLabel}
        geofeceFeature={feature}
        onFindNearyBy={onFindNearyBy}
        canEditGeofence={canEditGeofence}
        isFindNearbyPopup={isFindNearbyPopup}
        ancestorAccountIds={ancestorAccountIds}
        setHideGraphicsForEdit={setHideGraphicsForEdit}
        setEditGeofenceGraphic={setEditGeofenceGraphic}
        onFindButtonStateChange={onFindButtonStateChange}
        onFindNearbyPopupStatusChange={onFindNearbyPopupStatusChange}
        setFindNearbyPopupForGeofence={setFindNearbyPopupForGeofence}
        onFindNearbyBreadcrumbBackClick={onFindNearbyBreadcrumbBackClick} />,
      container
    );
    return container;
  }, [mapView, ancestorAccountIds, isTrackingV2]);
  
  return null;
};

export default LandmarkGeofenceLayer;
